import {
  DataTable,
  DateField,
  EditButton,
  EmailField,
  ReferenceManyField,
  Show,
  SimpleShowLayout,
  TextField,
} from "@/components/admin";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Briefcase, Calendar, Hash, Mail, User } from "lucide-react";
import { useEffect, useState } from "react";
import { useRecordContext } from "ra-core";
import { supabase } from "../../providers/supabase/supabase";
import "../HR.css";

type LeaveBalance = {
  leave_type_id: string;
  leave_type_name: string;
  leave_type_code: string;
  days_per_year: number;
  used_days: number;
  available_days: number;
};

const useLeaveBalance = (employeeId: string | undefined) => {
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!employeeId) return;
    setLoading(true);
    supabase
      .from("leave_balance")
      .select("leave_type_id,leave_type_name,leave_type_code,days_per_year,used_days,available_days")
      .eq("employee_id", employeeId)
      .then(({ data }) => {
        setBalances((data as LeaveBalance[]) ?? []);
        setLoading(false);
      });
  }, [employeeId]);

  return { balances, loading };
};

const EmployeeHeader = () => {
  const record = useRecordContext();
  if (!record) return null;
  const s = record.status || "active";
  return (
    <div className="flex justify-between items-start border-b pb-6 mb-2">
      <div className="flex gap-4 items-center">
        <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center border-2 border-primary/20">
          <User className="w-8 h-8 text-primary" />
        </div>
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-slate-900">{record.name}</h2>
            <div className={`status-bookmark ${s}`}>
              {s.replace("_", " ")}
            </div>
          </div>
          <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
            <span className="employee-code">
              {record.employee_code}
            </span>
            {record.designation_title && (
              <span className="font-medium text-slate-700">
                {record.designation_title}
              </span>
            )}
            {record.department_name && (
              <Badge variant="outline" className="text-xs">
                <Briefcase className="w-3 h-3 mr-1" />
                {record.department_name}
              </Badge>
            )}
          </div>
        </div>
      </div>
      <EditButton className="btn-green-action" />
    </div>
  );
};

const AttendanceStatusBadge = () => {
  const record = useRecordContext();
  if (!record) return null;
  const s = record.status || "present";
  return (
    <div className={`status-bookmark ${s}`}>
      {s.replace("_", " ")}
    </div>
  );
};

const LeaveStatusBadge = () => {
  const record = useRecordContext();
  if (!record) return null;
  const s = record.status || "pending";
  return (
    <div className={`status-bookmark ${s}`}>
      {s}
    </div>
  );
};

const LeaveBalanceSection = () => {
  const record = useRecordContext();
  const { balances, loading } = useLeaveBalance(record?.id as string | undefined);

  if (loading) return <div className="text-xs text-slate-400 py-4">Loading balances…</div>;
  if (!balances.length) return null;

  return (
    <div className="mb-6">
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
        Leave Balance — {new Date().getFullYear()}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {balances.map((b) => {
          const pct = b.days_per_year > 0 ? Math.round((b.used_days / b.days_per_year) * 100) : 0;
          const isLow = b.available_days <= 2 && b.days_per_year > 0;
          return (
            <div
              key={b.leave_type_id}
              className="rounded-lg border bg-card p-3 flex flex-col gap-2"
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  {b.leave_type_code}
                </span>
                {isLow && (
                  <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                    Low
                  </span>
                )}
              </div>
              <div className="text-sm font-medium text-slate-700">{b.leave_type_name}</div>
              <div className="flex items-end justify-between">
                <div>
                  <span className="text-2xl font-bold text-slate-900">{b.available_days}</span>
                  <span className="text-xs text-slate-400 ml-1">/ {b.days_per_year} days</span>
                </div>
                <span className="text-xs text-slate-400">{b.used_days} used</span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${pct >= 80 ? "bg-red-400" : pct >= 50 ? "bg-amber-400" : "bg-emerald-400"}`}
                  style={{ width: `${Math.min(pct, 100)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ProfileTab = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
      <Card>
        <CardHeader className="pb-3 border-b">
          <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
            <Mail className="w-3.5 h-3.5" /> Contact
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-3">
          <div>
            <span className="text-[10px] font-bold text-slate-400 uppercase block">
              Email
            </span>
            <EmailField
              source="email"
              className="text-sm text-blue-600"
              emptyText="—"
            />
          </div>
          <div>
            <span className="text-[10px] font-bold text-slate-400 uppercase block">
              Phone
            </span>
            <TextField source="phone" className="text-sm" emptyText="—" />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-3 border-b">
          <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
            <Briefcase className="w-3.5 h-3.5" /> Employment
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-3">
          <div>
            <span className="text-[10px] font-bold text-slate-400 uppercase block">
              Type
            </span>
            <TextField
              source="employment_type"
              className="text-sm capitalize"
              emptyText="—"
            />
          </div>
          <div>
            <span className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Joining Date
            </span>
            <DateField
              source="joining_date"
              className="text-sm"
              emptyText="—"
            />
          </div>
          {record.tenure_days != null && (
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase block">
                Tenure
              </span>
              <span className="text-sm">
                {Math.floor(record.tenure_days / 365)} yrs{" "}
                {Math.floor((record.tenure_days % 365) / 30)} mo
              </span>
            </div>
          )}
        </CardContent>
      </Card>
      {(record.pan || record.aadhaar || record.gender || record.dob) && (
        <Card className="md:col-span-2">
          <CardHeader className="pb-3 border-b">
            <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
              <Hash className="w-3.5 h-3.5" /> Personal Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase block">
                Gender
              </span>
              <TextField source="gender" className="text-sm" emptyText="—" />
            </div>
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase block">
                DOB
              </span>
              <DateField source="dob" className="text-sm" emptyText="—" />
            </div>
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase block">
                Blood Group
              </span>
              <TextField
                source="blood_group"
                className="text-sm"
                emptyText="—"
              />
            </div>
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase block">
                PAN
              </span>
              <TextField
                source="pan"
                className="employee-code"
                emptyText="—"
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export const EmployeeShow = () => (
  <Show title="Employee Details" actions={false}>
    <SimpleShowLayout>
      <EmployeeHeader />
      <Tabs defaultValue="profile" className="w-full">
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="attendance">Attendance</TabsTrigger>
          <TabsTrigger value="leave">Leave</TabsTrigger>
          <TabsTrigger value="payslips">Payslips</TabsTrigger>
        </TabsList>
        <TabsContent value="profile">
          <ProfileTab />
        </TabsContent>
        <TabsContent value="attendance">
          <div className="tab-content-container">
            <ReferenceManyField
              reference="hr_attendance"
              target="employee_id"
              sort={{ field: "date", order: "DESC" }}
              perPage={30}
              label={false}
            >
              <div className="employees-table-container">
                <DataTable className="employees-table">
                  <DataTable.Col source="date" label="Date">
                    <DateField source="date" />
                  </DataTable.Col>
                  <DataTable.Col label="Status">
                    <AttendanceStatusBadge />
                  </DataTable.Col>
                  <DataTable.Col source="check_in" label="Check In">
                    <DateField source="check_in" showTime />
                  </DataTable.Col>
                  <DataTable.Col source="check_out" label="Check Out">
                    <DateField source="check_out" showTime />
                  </DataTable.Col>
                  <DataTable.Col source="hours_worked" label="Hours" />
                  <DataTable.Col
                    source="source"
                    label="Source"
                    className="capitalize"
                  />
                </DataTable>
              </div>
            </ReferenceManyField>
          </div>
        </TabsContent>
        <TabsContent value="leave">
          <div className="tab-content-container">
            <LeaveBalanceSection />
            <ReferenceManyField
              reference="hr_leave_requests"
              target="employee_id"
              sort={{ field: "created_at", order: "DESC" }}
              label={false}
            >
              <div className="employees-table-container">
                <DataTable rowClick="show" className="employees-table">
                  <DataTable.Col source="from_date" label="From">
                    <DateField source="from_date" />
                  </DataTable.Col>
                  <DataTable.Col source="to_date" label="To">
                    <DateField source="to_date" />
                  </DataTable.Col>
                  <DataTable.Col source="days" label="Days" />
                  <DataTable.Col label="Status">
                    <LeaveStatusBadge />
                  </DataTable.Col>
                  <DataTable.Col source="reason" label="Reason" />
                </DataTable>
              </div>
            </ReferenceManyField>
          </div>
        </TabsContent>
        <TabsContent value="payslips">
          <div className="tab-content-container">
            <ReferenceManyField
              reference="hr_payroll_slips"
              target="employee_id"
              sort={{ field: "id", order: "DESC" }}
              label={false}
            >
              <div className="employees-table-container">
                <DataTable rowClick="show" className="employees-table">
                  <DataTable.Col
                    source="gross"
                    label="Gross"
                  />
                  <DataTable.Col source="deductions" label="Deductions" />
                  <DataTable.Col
                    source="net"
                    label="Net Pay"
                    className="amount-positive"
                  />
                  <DataTable.Col source="working_days" label="Working Days" />
                  <DataTable.Col source="present_days" label="Present Days" />
                </DataTable>
              </div>
            </ReferenceManyField>
          </div>
        </TabsContent>
      </Tabs>
    </SimpleShowLayout>
  </Show>
);

